import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAutorenew, MdSend } from 'react-icons/md'
import {
    getNewsletterSettings,
    listNewsletterSends,
    sendNewsletterTest,
    updateNewsletterSettings,
    type StoreNewsletterFrequency,
    type StoreNewsletterSend,
    type StoreNewsletterSettings,
} from '@/lib/api/commerce/store-newsletter'

const FREQUENCIES: { value: StoreNewsletterFrequency; label: string }[] = [
    { value: 'OFF', label: 'Desligado' },
    { value: 'WEEKLY', label: 'Toda semana' },
    { value: 'BIWEEKLY', label: 'A cada 15 dias' },
    { value: 'MONTHLY', label: 'Todo mês' },
]

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Envio automático da newsletter: a plataforma manda aos inscritos, sozinha, os produtos
 * novos e as promoções da loja na frequência escolhida. Sem novidade, não manda.
 */
export default function NewsletterAutomationCard({ storeId, canUpdate }: { storeId: number; canUpdate: boolean }) {
    const [settings, setSettings] = useState<StoreNewsletterSettings | null>(null)
    const [sends, setSends] = useState<StoreNewsletterSend[]>([])
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)

    const load = useCallback(() => {
        getNewsletterSettings(storeId).then((res) => res.success && res.result && setSettings(res.result))
        listNewsletterSends(storeId).then((res) => res.success && setSends(res.result ?? []))
    }, [storeId])

    useEffect(load, [load])

    const changeFrequency = async (frequency: StoreNewsletterFrequency) => {
        setSaving(true)
        try {
            const res = await updateNewsletterSettings(storeId, frequency)
            if (res.success && res.result) {
                setSettings(res.result)
                toast.success(frequency === 'OFF' ? 'Envio automático desligado' : 'Frequência salva')
            } else {
                toast.error(res.error || 'Não foi possível salvar')
            }
        } finally {
            setSaving(false)
        }
    }

    const sendTest = async () => {
        setTesting(true)
        try {
            const res = await sendNewsletterTest(storeId)
            if (res.success && res.result) toast.success(`E-mail de teste enviado para ${res.result.sentTo}`)
            else toast.error(res.error || 'Não foi possível enviar o teste')
        } finally {
            setTesting(false)
        }
    }

    return (
        <div className="flex flex-col gap-5 rounded-xl border border-base-300/50 bg-base-100 p-5 shadow-sm">
            <div className="flex flex-col gap-1">
                <h2 className="flex items-center gap-2 text-base font-semibold">
                    <MdAutorenew className="text-xl text-secondary" /> Envio automático
                </h2>
                <p className="text-sm text-base-content/60">
                    A plataforma manda aos inscritos os produtos que chegaram na loja e as promoções ativas, com a cara da loja
                    e o link para cancelar. Sem novidade desde o último envio, não manda. Sai às 10h, no dia do envio.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Frequência</span>
                    <select
                        className="select select-bordered w-full"
                        value={settings?.frequency ?? 'OFF'}
                        disabled={!canUpdate || saving || !settings}
                        onChange={(e) => changeFrequency(e.target.value as StoreNewsletterFrequency)}
                    >
                        {FREQUENCIES.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <span className="text-xs text-base-content/60">
                        {settings?.lastSentAt ? `Último envio em ${formatDateTime(settings.lastSentAt)}.` : 'Nenhum envio ainda.'}
                    </span>
                </label>
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Ver antes de ligar</span>
                    <button type="button" className="btn btn-outline gap-2" disabled={!canUpdate || testing} onClick={sendTest}>
                        <MdSend className="text-lg" />
                        {testing ? 'Enviando...' : 'Enviar teste para mim'}
                    </button>
                    <span className="text-xs text-base-content/60">Vai para o seu e-mail, como sairia hoje.</span>
                </div>
            </div>

            {sends.length > 0 && (
                <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-base-content/60">Envios</span>
                    <div className="overflow-x-auto">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th className="text-right">Enviados</th>
                                    <th className="text-right">Falhas</th>
                                    <th className="text-right">Produtos</th>
                                    <th className="text-right">Promoções</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sends.map((send) => (
                                    <tr key={send.id}>
                                        <td>{formatDateTime(send.sentAt)}</td>
                                        <td className="text-right">{send.recipients}</td>
                                        <td className={`text-right ${send.failed > 0 ? 'text-error' : ''}`}>{send.failed}</td>
                                        <td className="text-right">{send.products}</td>
                                        <td className="text-right">{send.promotions}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}
